/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToWishlistComponent, IWishlistActionErrorResult } from '@msdyn365-commerce/components';
import { format, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';
import {
    getConfigureErrors,
    IBuyboxAddToCartViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonResources,
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps
} from '@msdyn365-commerce-modules/buybox';
import { IMilBuyboxCallbacks, IMilBuyboxState } from './utilities';
import {
    ArrayExtensions,
    getFullAvailableInventoryNearby,
    GetFullAvailableInventoryNearbyInput
} from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { reaction } from 'mobx';
import * as React from 'react';
import { IMilBuyboxViewProps } from './mil-buybox';
import { IMilBuyboxProps, IMilBuyboxResources } from './mil-buybox.props.autogenerated';
import { IMilBuyboxData } from './mil-buybox.data';
import { renderAddToOrderTemplateButton } from './components/buybox-add-to-order-template';
import {
    IGetSingleUnitOfMeasureConversionResponse
    // IGetStockStatusResponse
} from '../../actions/DataServiceEntities.g';
import MilIncrementalQuantity from './components/mil-incremental-quantity';
import { InventHoldStatusType } from './utilities/InventHoldStatusEnum';
import { WarehouseInventoryDisplay } from '../../utils/InventoryDisplayHelper';
import UomConversionNode, { formatPricePerValues } from './utilities/UomConversion';

/**
 * BuyBoxConstants enum.
 */
export enum BuyBoxConstants {
    zero = 0
}

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
export const onChangeHandler = (callbacks: IBuyboxCallbacks | undefined) => (newValue: number): boolean => {
    if (callbacks?.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

/**
 * RenderQuantity.
 * @param quantityComponent - QuantityComponent.
 * @param callbacks - Callbacks.
 * @param props - Props.
 * @param state - State.
 * @param extentionResources - ExtentionResources.
 * @param quantityLimitsMessages - QuantityLimitsMessages.
 * @param max - Max.
 * @param telemetryContent - TelemetryContent.
 * @param unitOfMeasure - Unit Of Measure.
 * @returns -- Returns.
 */
export const renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IMilBuyboxCallbacks,
    props: IMilBuyboxProps<IMilBuyboxData>,
    state: IMilBuyboxState,
    extentionResources: IMilBuyboxResources,
    formatPricePerValues: (value: number | undefined, minDecimals: number, maxDecimals: number) => string | undefined,
    quantityLimitsMessages: React.ReactNode,

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    max: number | undefined,
    defaultMaxQty: number,
    telemetryContent?: ITelemetryContent,
    unitOfMeasure?: React.ReactNode,
    uomConversionData?: IGetSingleUnitOfMeasureConversionResponse
): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;
    const {
        resources,
        data: { uomConversion },
        context: {
            app: {
                config: { displayMinimumQuantity, displayMaximumQuantity, displayMultipleOfQuantity }
            }
        }
    } = props;
    const { quantity } = state;

    const uomRes = uomConversion.result?.uomConversion;
    const smallUomDesc = uomRes?.SmallUomDescription;
    const largeUomDesc = uomRes?.LargeUomDescription;
    const convFactor = uomRes?.ConversionFactor;

    const multipleAmount = props.data.product.result?.Behavior?.MultipleOfQuantity || 1;
    const minConfigAmount = props.data.product.result?.Behavior?.MinimumQuantity || 1;
    const minAmount = minConfigAmount < multipleAmount ? multipleAmount : minConfigAmount;
    const maxSetting = props.data.product.result?.Behavior?.MaximumQuantity;
    const maxAmount =
        (maxSetting && maxSetting % multipleAmount > 0 ? Math.trunc(maxSetting - (maxSetting % multipleAmount)) : maxSetting) ||
        (max && max % multipleAmount > 0 ? Math.trunc(max - (max % multipleAmount)) : max) ||
        defaultMaxQty;
    const uomLabelText =
        largeUomDesc && largeUomDesc.toLocaleLowerCase().includes('per')
            ? resources.uomInfoLabelText.toLocaleLowerCase().replace('per', '')
            : resources.uomInfoLabelText;
    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <MilIncrementalQuantity
                id='ms-buybox__product-quantity-input'
                max={maxAmount}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                min={minAmount}
                multiple={multipleAmount}
            />
            {unitOfMeasure}
            {/* {quantityLimitsMessages} */}
            {/* 19790 - UOM */}
            {smallUomDesc && largeUomDesc && convFactor && uomConversionData && (
                <div className='ms-buybox__uom-info msi-InfoCircle'>
                    {format(uomLabelText, formatPricePerValues(convFactor, 0, 0), smallUomDesc, largeUomDesc)}
                </div>
            )}
            <div className='ms-buybox__default-order-details-container'>
                {displayMinimumQuantity && (
                    <div className='ms-buybox__default-order-details-container--min'>
                        <span className='ms-buybox__default-order-details-container--min-label'>
                            {props.resources.minimumOrderQtyLabelText}{' '}
                        </span>
                        {minAmount}
                    </div>
                )}

                {displayMaximumQuantity && (
                    <div className='ms-buybox__default-order-details-container--min'>
                        <span className='ms-buybox__default-order-details-container--min-label'>
                            {props.resources.maximumOrderQtyLabelText}{' '}
                        </span>
                        {maxAmount}
                    </div>
                )}

                {displayMultipleOfQuantity && (
                    <div className='ms-buybox__default-order-details-container--mult'>
                        <span className='ms-buybox__default-order-details-container--mult-label'>
                            {props.resources.multipleOfLabelText}{' '}
                        </span>
                        {multipleAmount}
                    </div>
                )}
            </div>
        </Node>
    );
};

/**
 * Render key in price function.
 * @param keyInPrice - KeyInPrice.
 * @returns -- Returns.
 */
const renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

/**
 * RenderAddToCart.
 * @param addToCart - AddToCart.
 * @returns -- Returns.
 */
const renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

/**
 * RenderFindInStore.
 * @param findInStore - FindInStore.
 * @returns -- Returns.
 */
const renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * RenderAddToOrderTemplate.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToOrderTemplate = (
    props: IMilBuyboxViewProps & IMilBuyboxProps<IMilBuyboxData>,
    state: IMilBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToOrderTemplate || !product) {
        return null;
    }

    const updatedState = { ...state, quantity: state.quantity || 1 };

    const orderTemplateButton = renderAddToOrderTemplateButton(props, updatedState, callbacks, product);
    // const orderTemplateButton = renderAddToOrderTemplateButton(props, state, callbacks, product);

    return (
        <Node {...props.addToOrderTemplate.ContainerProps}>
            {props.addToOrderTemplate.errorBlock}
            {orderTemplateButton}
        </Node>
    );
};

/**
 * Add to wishlist failed function.
 * @param callbacks -Buybox callbacks.
 * @param resources -Buybox resources.
 * @param product -Simple product.
 * @returns Update error state.
 */
const onAddToWishlistFailed = (callbacks: IBuyboxCallbacks, resources: IBuyboxCommonResources, product: SimpleProduct | undefined) => (
    result: IWishlistActionErrorResult
) => {
    callbacks.updateErrorState({
        errorHost: 'WISHLIST',
        configureErrors:
            result.status === 'MISSINGDIMENSION' ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard) : {}
    });
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @param product - Product data.
 * @returns -- Returns.
 */
const renderAddtoWishlistButton = (
    props: IMilBuyboxViewProps & IMilBuyboxProps<IMilBuyboxData>,
    state: IMilBuyboxState,
    callbacks: IBuyboxCallbacks,
    product: SimpleProduct
): React.ReactNode => {
    const { context, resources } = props;
    const wishlists = props.data.wishlists.result;
    const isShowWishlitButtonText = true;
    return (
        <AddToWishlistComponent
            className='msc-add-to-cart-extra-actions'
            addToWishlistButtonText={resources.addToWishlistButtonText}
            removeFromWishlistButtonText={resources.removeFromWishlistButtonText}
            addToWishlistMessage={resources.addToWishlistMessage}
            removedFromWishlistMessage={resources.removedFromWishlistMessage}
            addItemToWishlistError={resources.addItemToWishlistError}
            removeItemFromWishlistError={resources.removeItemFromWishlistError}
            nameOfWishlist={resources.nameOfWishlist}
            data={{ product, wishlists }}
            context={context}
            ariaRole='button'
            id={props.id}
            typeName={props.typeName}
            onError={onAddToWishlistFailed(callbacks, resources, product)}
            getSelectedProduct={state.selectedProduct}
            showButtonText={isShowWishlitButtonText}
            showButtonTooltip={false}
        />
    );
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToWishlist = (
    props: IMilBuyboxViewProps & IMilBuyboxProps<IMilBuyboxData>,
    state: IMilBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToWishlist || !product) {
        return null;
    }

    if (!props.config.enableWishlist) {
        return null;
    }

    const wishlistButton = renderAddtoWishlistButton(props, state, callbacks, product);
    return (
        <Node {...props.addToWishlist.ContainerProps}>
            {props.addToWishlist.errorBlock}
            {wishlistButton}
        </Node>
    );
};

/**
 * RRenderShopSimilarItem.
 * @param shopSimilarItem - ShopSimilarItem.
 * @returns -- Returns.
 */
const renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

/**
 * RenderSocialShare.
 * @param socialShare - Social share react node array.
 * @returns -- Returns.
 */
const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

/**
 * RenderConfigureDropdown.
 * @param dropdown - Buybox product configure dropdown view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};

/**
 * RenderConfigure.
 * @param configure - Buybox product configure view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderConfigureDropdown)}</Node>;
};

/**
 * BuyboxView.
 * @param props - Props.
 * @returns -- Returns.
 */
const BuyboxView: React.FC<IMilBuyboxViewProps & IMilBuyboxProps<IMilBuyboxData>> = props => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const {
        ModuleProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        MediaGalleryContainerProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        productComparisonButton,
        configure,
        description,
        findInStore,
        // price,
        quantity,
        title,
        // rating,
        shopSimilarLook,
        keyInPrice,
        shopSimilarDescription,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        max,
        defaultMaxQty,
        unitOfMeasure,
        callbacks,
        state,
        resources,
        quantityLimitsMessages,
        telemetryContent,
        app: { config: appConfig },
        context: {
            app: {
                config: {
                    desiredNonstockCommerceName,
                    nonstockCommerceState,
                    surchargeLabel,
                    maximumDecimalsEcommPrice,
                    maximumDecimalsFyiPrice,
                    displayStockStatus
                }
            }
        },
        catalogs,
        guaranteedItem
    } = props;

    const displayedSurchargeLabel = surchargeLabel ? surchargeLabel : 'Surcharge incl.';
    let skuText: string = props.resources.skuText;
    skuText += props.data.product.result?.ItemId;
    const preferredStoreInfo: IStoreInfo | undefined = props.data.storeSelectorStateManager.result?.preferredStore;
    const preferredStoreId = preferredStoreInfo?.StoreId ? preferredStoreInfo.StoreId : '';
    const preferredStoreName = preferredStoreInfo?.StoreName ? preferredStoreInfo.StoreName : '';

    const [isStoreChange, setIsStoreChange] = React.useState<boolean>(false);
    const [uomConversionData, setUomConversionData] = React.useState<IGetSingleUnitOfMeasureConversionResponse>();

    const getInventory = React.useCallback(async () => {
        const input = new GetFullAvailableInventoryNearbyInput(
            props.data.product.result?.RecordId,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            true
        );

        await getFullAvailableInventoryNearby(input, props.context.actionContext).catch((error: Error) => {
            props.telemetry.error(error.message);
        });
        setIsStoreChange(false);
    }, [
        props.data.product.result?.RecordId,
        appConfig.enableStockCheck,
        preferredStoreId,
        preferredStoreName,
        props.context.actionContext,
        props.resources.availabilityAtPreferredStoreText,
        props.resources.inStockText,
        props.resources.outOfStockText,
        props.telemetry
    ]);

    React.useEffect(() => {
        if (props.config.displayStockAvailability) {
            getInventory().catch((error: Error) => {
                props.telemetry.error(error.message);
            });
        }
    }, [getInventory, isStoreChange, props.telemetry, props.config.displayStockAvailability]);

    reaction(
        () => props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
        () => {
            setIsStoreChange(true);
        }
    );

    React.useEffect(() => {
        const uomData = props.data.uomConversion.result?.uomConversion;
        if (uomData) {
            setUomConversionData(uomData);
        }
    }, []);

    const renderStockHoldStatus = (): JSX.Element | null => {
        /* 22425 - Billhold and Holdstock */
        const {
            resources: { billHoldLabelText, holdStockLabelText }
        } = props;

        const stockStatus = props.state?.stockHoldStatusData?.StockHoldStatuses;
        if (stockStatus && stockStatus.length > 0) {
            return (
                <div className='ms-buybox__stock-status-container'>
                    {stockStatus &&
                        stockStatus.map((shs, i) => {
                            if (shs.InventHoldStatus === InventHoldStatusType.BillHold) {
                                return (
                                    <div key={i} className='ms-buybox__stock-status-container--label'>
                                        {`${billHoldLabelText} `}
                                        <span className='ms-buybox__stock-status-container--quantity'>{shs.Quantity}</span>
                                    </div>
                                );
                            }
                            return;
                        })}
                    {stockStatus &&
                        stockStatus.map((shs, i) => {
                            if (shs.InventHoldStatus === InventHoldStatusType.HoldStock) {
                                return (
                                    <div key={i} className='ms-buybox__stock-status-container--label'>
                                        {`${holdStockLabelText} `}
                                        <span className='ms-buybox__stock-status-container--quantity'>{shs.Quantity}</span>
                                    </div>
                                );
                            }
                            return;
                        })}
                </div>
            );
        }
        return null;
    };

    const renderWarehouseInventory = (): JSX.Element => {
        const {
            resources: { availableStockForPickupLabel, availableStockForDeliveryLabel },
            state: { cutoffTime, receiptCalendar, totalInventoryAvail },
            context: {
                app: { config }
            }
        } = props;
        const warehouses = props.state.custWhInvAvail ?? [];

        return (
            <>
                <WarehouseInventoryDisplay
                    resources={{ availableStockForDeliveryLabel, availableStockForPickupLabel }}
                    state={{
                        cutoffTime,
                        receiptCalendar,
                        custWhInvAvail: warehouses,
                        totalInventoryAvail
                    }}
                    config={config}
                    itemId={props.data.product.result?.ItemId || ''}
                    isAuthenticated={props.context.actionContext.requestContext.user.isAuthenticated}
                />
                {renderStockHoldStatus()}
            </>
        );
    };

    /* 19796 - Safety Data Sheets */

    const renderSafetyDataSheetLink = (): JSX.Element | null => {
        const {
            resources: { safetyDataSheetLinkText },
            config: { sdsLinkAttributeKey, enableSdsLink, omnichannelSdsMediaPurposeName },
            data: { additionalMediaLocations, productSpecificationData }
        } = props;

        if (!enableSdsLink) {
            return null;
        }

        const productAttributes = productSpecificationData.result;
        const safetyDataLinkAttribute = productAttributes && productAttributes.find(attr => attr?.Name === sdsLinkAttributeKey);

        const omniSdsMedia = additionalMediaLocations.result;
        const safetyDataOmniLink = omniSdsMedia && omniSdsMedia.find(media => media.MediaPurpose === omnichannelSdsMediaPurposeName);

        const sdsLinkToUse = safetyDataOmniLink?.Uri || safetyDataLinkAttribute?.TextValue;
        return (
            <>
                {sdsLinkToUse ? (
                    <div className='ms-buybox__safety-data-container'>
                        <a className='ms-buybox__safety-data-container--link' href={sdsLinkToUse} target='_blank' rel='noreferrer'>
                            {safetyDataSheetLinkText}
                        </a>
                    </div>
                ) : null}
            </>
        );
    };

    let stockStatus = props.data.stockStatus.result?.stockStatus;

    if (stockStatus && stockStatus.toLocaleLowerCase() === nonstockCommerceState?.toLocaleLowerCase() && desiredNonstockCommerceName) {
        stockStatus = desiredNonstockCommerceName;
    }

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>
                {props.mediaGallery}
                {/* 19790 - Guaranteed Item */}
                {guaranteedItem}
            </Node>
            <Node {...ProductInfoContainerProps}>
                {catalogs && catalogs[0].Name}
                {title}
                <Node className='msc-buybox__ratings-section'>
                    <div className='msc-buybox__ratings-section-sku-text'>{skuText}</div>
                    {displayStockStatus && stockStatus && (
                        <div className='msc-buybox__ratings-section-stock-status'>{`${resources.stockStatusText} ${stockStatus}`}</div>
                    )}
                    {/* {rating} */}
                </Node>
                {/* 19790 - UomConversion */}
                <Node className='msc-buybox__price-section'>
                    <UomConversionNode
                        productPrice={props.state.productPrice}
                        uomConversionData={uomConversionData}
                        pricePerLabelText={props.resources.pricePerLabelText}
                        unitPricePerLabelText={props.resources.unitPricePerLabelText}
                        thousandText={props.resources.thousandText}
                        displayedSurchargeLabel={displayedSurchargeLabel}
                        maximumDecimalsEcommPrice={maximumDecimalsEcommPrice}
                        maximumDecimalsFyiPrice={maximumDecimalsFyiPrice}
                        className='msc-buybox'
                        currencyFormatter={props.context.cultureFormatter.formatCurrency}
                        freePriceText={resources.priceFree}
                    />
                </Node>
                {configure && renderConfigure(configure)}
                {keyInPrice && renderKeyInPrice(keyInPrice)}
                <Node className='msc-buybox__bulk-purchase-section'>
                    <div className='msc-buybox__bulk-purchase-button-text'>
                        {quantity &&
                            renderQuantity(
                                quantity,
                                callbacks,
                                props,
                                state,
                                resources,
                                formatPricePerValues,
                                quantityLimitsMessages,
                                max,
                                defaultMaxQty,
                                telemetryContent,
                                unitOfMeasure,
                                uomConversionData
                            )}
                        {props.bulkPurchaseLink}
                    </div>
                </Node>
                {/* 19790 - True Inventory */}
                {renderWarehouseInventory()}
                {renderAddToCart(addToCart)}
                {findInStore && renderFindInStore(findInStore)}
                {addToWishlist && renderAddToWishlist(props, state, callbacks)}
                {productComparisonButton}
                {addToOrderTemplate && renderAddToOrderTemplate(props, state, callbacks)}
                {/* 19796 - Safety Data Sheets */}
                {renderSafetyDataSheetLink()}
                {findInStore?.productPickupOptionList ? (
                    <div className='msc-buybox__pickup-options'>{findInStore.productPickupOptionList}</div>
                ) : null}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                <div className='msc-buybox__description-text'>{props.resources.shopText}</div>
                <Node className='msc-buybox__shop-description'>
                    {shopSimilarLook && renderShopSimilarItem(shopSimilarLook)}
                    {shopSimilarDescription && renderShopSimilarItem(shopSimilarDescription)}
                </Node>
                <Node className='msc-buybox__description-section'>
                    <div className='msc-buybox__description-section-text'>{props.resources.descriptionText}</div>
                    {description}
                </Node>
                {ArrayExtensions.hasElements(props.slots.productSpecification) ? (
                    <Node className='msc-buybox__product-specification'>{props.slots.productSpecification[0]}</Node>
                ) : null}
            </Node>
        </Module>
    );
};

export default BuyboxView;
